import { useState, useEffect } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Tag, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface ProductType {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isActive: boolean;
}

function generateSlug(name: string): string {
  return name.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function ProductTypesSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", slug: "", description: "", isActive: true });

  const { data: productTypes = [] } = useQuery<ProductType[]>({
    queryKey: ["/api/product-types", { includeInactive: true }],
    queryFn: () => api.get<ProductType[]>(`/product-types?includeInactive=true`, sessionId),
  });

  // Auto-generate slug from name when creating
  useEffect(() => {
    if (!editingId && form.name) {
      setForm(f => ({ ...f, slug: generateSlug(f.name) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, editingId]);

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/product-types/${editingId}`, form, sessionId)
      : api.post(`/product-types`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/product-types"] });
      setDialogOpen(false);
      setEditingId(null);
      toast({ title: editingId ? "Product type updated" : "Product type created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/product-types/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/product-types"] });
      toast({ title: "Product type deleted" });
    },
  });

  const handleEdit = (pt: ProductType) => {
    setEditingId(pt.id);
    setForm({ name: pt.name, slug: pt.slug, description: pt.description || "", isActive: pt.isActive });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({ name: "", slug: "", description: "", isActive: true });
    setDialogOpen(true);
  };

  const handleDelete = (pt: ProductType) => {
    if (confirm(`Delete product type "${pt.name}"?`)) {
      deleteMutation.mutate(pt.id);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Product Types"
        description="Classify products by their type (e.g. raw material, finished goods, service)"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-product-type">
              <Plus className="h-4 w-4 mr-1" /> Add Product Type
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Tag className="h-4 w-4" /> All Product Types ({productTypes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {productTypes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No product types configured.</p>
          ) : (
            <div className="space-y-2">
              {productTypes.map((pt) => (
                <div
                  key={pt.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{pt.name}</span>
                      <Badge variant="outline" className="text-[10px] font-mono">{pt.slug}</Badge>
                      {!pt.isActive && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                    </div>
                    {pt.description && <p className="text-xs text-muted-foreground mt-0.5">{pt.description}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(pt)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(pt)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Product Type" : "New Product Type"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Raw Material"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Slug *</label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="raw-material"
              />
              <p className="text-xs text-muted-foreground mt-1">Lowercase letters, numbers and hyphens only</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.name || !form.slug || saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
